// src/services/error/ErrorManager.ts

/**
 * Represents the severity of an error.
 */
export enum ErrorSeverity {
  INFO = 'info',       // Informational message, not necessarily an error
  WARNING = 'warning', // Potential issue, but the application can continue
  ERROR = 'error',     // A definite error occurred, impacting functionality
  CRITICAL = 'critical' // A critical error, potentially halting the application
}

/**
 * Represents structured error information.
 */
export interface ErrorInfo {
  message: string;          // Human-readable error message
  severity: ErrorSeverity;  // Severity level
  component?: string;       // Optional: Name of the component/service where the error originated
  details?: any;            // Optional: Additional technical details (e.g., stack trace, error object)
  timestamp: Date;          // Timestamp when the error was reported
}

/**
 * Centralized service for reporting and handling application errors.
 * Currently provides basic logging but can be expanded for more complex handling
 * (e.g., sending reports to a server, displaying user notifications).
 */
class ErrorManager {
  private reportedErrors: ErrorInfo[] = []; // Simple in-memory log for now

  /**
   * Reports an error to the central manager.
   *
   * @param {string} message - The primary error message.
   * @param {ErrorSeverity} severity - The severity of the error.
   * @param {string} [component] - The source component/service.
   * @param {any} [details] - Additional error details.
   */
  reportError(message: string, severity: ErrorSeverity, component?: string, details?: any): void {
    const errorInfo: ErrorInfo = {
      message,
      severity,
      component,
      details,
      timestamp: new Date(),
    };

    this.reportedErrors.push(errorInfo);

    // Basic console logging based on severity
    switch (severity) {
      case ErrorSeverity.INFO:
        console.info(`[${component || 'App'}] INFO: ${message}`, details ? { details } : '');
        break;
      case ErrorSeverity.WARNING:
        console.warn(`[${component || 'App'}] WARNING: ${message}`, details ? { details } : '');
        break;
      case ErrorSeverity.ERROR:
        console.error(`[${component || 'App'}] ERROR: ${message}`, details ? { details } : '');
        break;
      case ErrorSeverity.CRITICAL:
        console.error(`[${component || 'App'}] CRITICAL: ${message}`, details ? { details } : '');
        // Potentially trigger more drastic actions for critical errors
        break;
      default:
        console.log(`[${component || 'App'}] LOG: ${message}`, details ? { details } : '');
    }

    // TODO: Implement further handling (e.g., UI notifications, server logging)
  }

  /**
   * Retrieves all reported errors (primarily for debugging).
   * @returns {ErrorInfo[]} A copy of the reported errors array.
   */
  getReportedErrors(): ErrorInfo[] {
    return [...this.reportedErrors]; // Return a copy
  }
}

// Export a singleton instance of the ErrorManager
export const errorManager = new ErrorManager();
